import React, { useState } from 'react';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Heading,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Text,
  Button,
  DownloadIcon,
  Dropdown,
  SelectInput,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  TablePagination,
  Amount,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import { usePagination } from 'hooks/usePagination';
import { MONTHS_LIST } from 'utils/Dates';
import api from 'api';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';

const MONTH_OPTIONS = MONTHS_LIST.map((label, index) => ({ label, value: String(index + 1) }));

export const OvertimeReport = () => {
  const currentMonth = String(new Date().getMonth() + 1);
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);

  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const payrollMonth = `${currentYear}-${selectedMonth.padStart(2, '0')}`;

  const {
    data: summaryData,
    isLoading,
    isFetching,
    refetch,
    isError,
  } = useQuery(
    ['overtimeSummary', payrollMonth, currentPage, itemsPerPage],
    () =>
      api.overtime.queries.getOvertimeSummary({
        input: {
          payrollMonth,
          page: currentPage + 1,
          pageSize: itemsPerPage,
        },
      }),
    { keepPreviousData: true },
  );

  const responseData = summaryData?.overtimeGetSummary?.data;
  const reportData = responseData?.data ?? [];
  const totalCount = responseData?.total ?? 0;

  const selectedMonthLabel = MONTH_OPTIONS.find((m) => m.value === selectedMonth)?.label ?? '';

  const isLoadingData = isLoading || isFetching;

  const hasError = !isLoadingData && isError;

  const shouldShowEmptyState = !isLoadingData && !hasError && totalCount === 0;

  return (
    <Box padding="spacing.8">
      <Breadcrumb marginBottom="spacing.5">
        <BreadcrumbItem href="/reports">Reports</BreadcrumbItem>
        <BreadcrumbItem href="/reports/overtime-report" isCurrentPage>
          Overtime Report
        </BreadcrumbItem>
      </Breadcrumb>

      <Box display="flex" justifyContent="space-between" alignItems="end" marginBottom="spacing.6">
        <Heading size="large" color="surface.text.gray.normal">
          Overtime Report
        </Heading>
        <Box display="flex" gap="spacing.4" alignItems="center">
          <Box width="200px">
            <Dropdown selectionType="single">
              <SelectInput
                label="Month"
                labelPosition="inside-input"
                accessibilityLabel="Choose month"
                name="month"
                value={selectedMonthLabel}
                onChange={({ values }) => {
                  const month = MONTH_OPTIONS.find((m) => m.label === values[0]);
                  if (month) {
                    setSelectedMonth(month.value);
                    setCurrentPage(0);
                  }
                }}
              />
              <DropdownOverlay>
                <ActionList>
                  {MONTH_OPTIONS.map((month) => (
                    <ActionListItem key={month.value} title={month.label} value={month.label} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          </Box>
          {totalCount > 0 && (
            <Button
              icon={DownloadIcon}
              iconPosition="left"
              variant="secondary"
              onClick={() => {
                //TODO_overtime: Intgegrate API for downloading report
              }}>
              Download Report
            </Button>
          )}
        </Box>
      </Box>

      {hasError ? (
        <ErrorView borderWidth="none" onRefetch={refetch} />
      ) : shouldShowEmptyState ? (
        <EmptyListView
          backgroundColor={'transparent'}
          title=""
          description="No overtime data found for the selected month."
        />
      ) : (
        <Table
          data={{ nodes: reportData.map((entry) => ({ ...entry, id: entry.userId })) }}
          isLoading={isLoading}
          isRefreshing={isFetching}
          gridTemplateColumns="1fr 2fr 1fr 1fr 1.5fr"
          pagination={
            <TablePagination
              paginationType="server"
              showPageSizePicker
              currentPage={currentPage}
              totalItemCount={totalCount}
              onPageChange={({ page }) => setCurrentPage(page)}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
            />
          }>
          {(nodes) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Employee ID</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Days Worked</TableHeaderCell>
                  <TableHeaderCell>Overtime Hours</TableHeaderCell>
                  <TableHeaderCell>Overtime Amount (INR)</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {nodes.map((row) => (
                  <TableRow key={row.id} item={row}>
                    <TableCell>
                      <Text>{row.empId}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{row.name}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{row.workingDays}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{row.totalOtHours}</Text>
                    </TableCell>
                    <TableCell>
                      <Amount value={row.totalOtAmountPaise / 100} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}
    </Box>
  );
};
